import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useFirebaseConnect, useFirestoreConnect, isLoaded } from 'react-redux-firebase'

import {
    createDictionaryWord,
    updateDictionaryWord,
    deleteDictionaryWord,
    languageOptions
} from 'modules'

import { useAuth, useNotification } from 'hooks'
import { useRoles } from './use-roles'

const initialTypes = [
    { name: 'Competitor', value: 'competitor' },
    { name: 'Customer', value: 'customer' },
    { name: 'Feature', value: 'feature' },
    { name: 'Industry Term', value: 'industry' },
    { name: 'Product', value: 'product' },
    { name: 'Name', value: 'name' },
    { name: 'Other', value: 'other' }
]

const upperCasesDictionaries = ['product', 'customer', 'competitor', 'name']

const roleValue = 'role'

const defaultLangOption = { label: 'English', value: 'en' }

const generateData = initialData => {
    const initialPhrase = initialData.phrase.trim()

    const transformPhrase = upperCasesDictionaries.includes(initialData.description)

    const phrase = transformPhrase
        ? initialPhrase[0].toUpperCase() + initialPhrase.slice(1)
        : initialPhrase.toLowerCase()

    return Object.fromEntries(
        Object.entries({
            ...initialData,
            phrase
        }).filter(([_, val]) => val)
    )
}

export const useDictionary = () => {
    const { orgId, uid } = useAuth()
    const { showSuccessNotification, showErrorNotification } = useNotification()

    const { isViewer } = useRoles()

    const userRoleId = useSelector(state => state.firebase.data.userRoleId)

    useFirestoreConnect({
        collection: 'users',
        doc: uid,
        storeAs: 'myprofile'
    })

    useFirebaseConnect({
        path: `onboarding/${uid}/userRole`,
        storeAs: 'userRoleId'
    })

    useFirestoreConnect({
        collection: userRoleId ? 'userRoles' : 'empty',
        storeAs: 'userRoles'
    })

    useFirestoreConnect({
        collection: !isViewer ? 'customPhrases' : 'empty',
        where: ['orgId', '==', orgId]
    })

    const { organizations, customPhrases, userRoles } = useSelector(state => state.firestore.data)

    const userRoleData = userRoles?.[userRoleId]

    const languages = useMemo(() => {
        const defaultLang = organizations?.[orgId]?.defaultLanguage

        const result = languageOptions.find(it => defaultLang === it.value)

        return [defaultLangOption, result].filter(Boolean).map(it => ({
            name: it.label,
            value: it.value
        }))
    }, [orgId, organizations])

    const isLoading = useMemo(() => {
        return (
            !isLoaded(userRoles) ||
            !isLoaded(userRoleId) ||
            (languages.length > 0 && !languages.some(Boolean))
        )
    }, [userRoles, userRoleId, languages])

    const dictionariesTypes = useMemo(() => {
        if (!userRoleData) return initialTypes

        return [...initialTypes, { ...userRoleData, value: roleValue }].sort((a, b) =>
            a?.name?.toLowerCase() > b?.name?.toLowerCase() ? 1 : -1
        )
    }, [userRoleData])

    const showError = useCallback(() => {
        showErrorNotification('Something went wrong')
    }, [showErrorNotification])

    const userRoleDataId = userRoleData?.id
    const checkToAddRoleId = useCallback(
        ({ description }) => {
            if (description !== roleValue || !userRoleDataId) return {}

            return { roleId: userRoleDataId }
        },
        [userRoleDataId]
    )

    const createPhrase = useCallback(
        data => {
            return createDictionaryWord({
                uid,
                orgId,
                ...generateData(data),
                ...checkToAddRoleId(data)
            })
                .then(() => showSuccessNotification('Item was added'))
                .catch(showError)
        },
        [orgId, uid, checkToAddRoleId, showError, showSuccessNotification]
    )

    const updatePhrase = useCallback(
        data => {
            return updateDictionaryWord({
                ...generateData({ ...data, orgId }),
                ...checkToAddRoleId(data)
            })
                .then(() => showSuccessNotification('Word was changed'))
                .catch(showError)
        },
        [orgId, checkToAddRoleId, showError, showSuccessNotification]
    )

    const deletePhrase = useCallback(
        deleteId => {
            return deleteDictionaryWord(deleteId)
                .then(() => showSuccessNotification('Word was removed'))
                .catch(showError)
        },
        [showError, showSuccessNotification]
    )

    return useMemo(
        () => ({
            isLoading,
            dictionary: customPhrases,
            dictionariesTypes,
            languageOptions: languages,
            createPhrase,
            updatePhrase,
            deletePhrase
        }),
        [
            isLoading,
            customPhrases,
            dictionariesTypes,
            languages,
            createPhrase,
            updatePhrase,
            deletePhrase
        ]
    )
}
